import api from './api'

export const ORDER_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CONFIRMATION: 'confirmation',
  APPOINTMENT_BOOKED: 'appointment_booked',
  COMPLETED: 'completed',
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
}

export const SERVICE_STATUS_FLOW = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.CONFIRMATION,
  ORDER_STATUS.APPOINTMENT_BOOKED,
  ORDER_STATUS.COMPLETED,
]

export const PRODUCT_STATUS_FLOW = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.SHIPPED,
  ORDER_STATUS.DELIVERED,
]

export const STATUS_LABELS = {
  pending: 'Pending',
  confirmation: 'Confirmation',
  appointment_booked: 'Appointment booked',
  completed: 'Completed',
  confirmed: 'Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  failed: 'Failed',
}

const TERMINAL_STATUSES = new Set([
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.DELIVERED,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.FAILED,
])

export const normalizeOrderStatus = (status) =>
  String(status || ORDER_STATUS.PENDING).toLowerCase().trim()

export const resolveFlowStatus = (order) => {
  const current = normalizeOrderStatus(order?.status)
  if (isServiceOrder(order) && current === ORDER_STATUS.CONFIRMED) {
    return ORDER_STATUS.CONFIRMATION
  }
  return current
}

export const getOrderStatusLabel = (status) =>
  STATUS_LABELS[normalizeOrderStatus(status)] ||
  String(status || 'Pending').replace(/_/g, ' ')

const SERVICE_ONLY_STATUSES = new Set([
  ORDER_STATUS.CONFIRMATION,
  ORDER_STATUS.APPOINTMENT_BOOKED,
])

const itemTypeFromRecord = (record) => {
  if (!record) return ''
  return String(
    record.item_type ||
    record.order_type ||
    record.product_type ||
    record.type ||
    record.product?.item_type ||
    record.product?.product_type ||
    ''
  ).toLowerCase()
}

export const getOrderPrimaryItemType = (order) => {
  if (!order) return 'product'

  const explicit = itemTypeFromRecord(order)
  if (explicit === 'service' || explicit === 'product') {
    return explicit
  }

  const items = order.items || []
  for (const item of items) {
    const type = itemTypeFromRecord(item)
    if (type === 'service') return 'service'
  }

  const status = normalizeOrderStatus(order.status)
  if (SERVICE_ONLY_STATUSES.has(status)) {
    return 'service'
  }

  for (const item of items) {
    const type = itemTypeFromRecord(item)
    if (type === 'product') return 'product'
  }

  return 'product'
}

export const isServiceOrder = (order) => getOrderPrimaryItemType(order) === 'service'

export const getStatusFlow = (order) =>
  isServiceOrder(order) ? SERVICE_STATUS_FLOW : PRODUCT_STATUS_FLOW

export const getNextPatchStatus = (order) => {
  if (!order) return null

  const flow = getStatusFlow(order)
  const current = resolveFlowStatus(order)
  if (TERMINAL_STATUSES.has(current)) return null

  const idx = flow.indexOf(current)
  if (idx === -1 || idx >= flow.length - 1) return null

  const next = flow[idx + 1]

  // Service orders reach appointment_booked via create_appointment, not PATCH.
  if (isServiceOrder(order) && next === ORDER_STATUS.APPOINTMENT_BOOKED) {
    return null
  }

  return next
}

export const getStatusActionLabel = (nextStatus) => {
  switch (nextStatus) {
    case ORDER_STATUS.CONFIRMATION:
    case ORDER_STATUS.CONFIRMED:
      return 'Confirm Order'
    case ORDER_STATUS.PROCESSING:
      return 'Start Processing'
    case ORDER_STATUS.SHIPPED:
      return 'Mark as Shipped'
    case ORDER_STATUS.DELIVERED:
      return 'Mark as Delivered'
    case ORDER_STATUS.COMPLETED:
      return 'Mark as Completed'
    default:
      return `Move to ${getOrderStatusLabel(nextStatus)}`
  }
}

export const canAdvanceOrderStatus = (order) => Boolean(getNextPatchStatus(order))

export const hasOrderAppointment = (order) => {
  if (!order) return false
  const status = normalizeOrderStatus(order.status)
  if (status === ORDER_STATUS.APPOINTMENT_BOOKED) return true

  const appointmentStatus = order.appointment_status
  return Boolean(
    order.appointment_id ||
    appointmentStatus === 'scheduled' ||
    appointmentStatus === 'appointment_booked' ||
    appointmentStatus === 'sent'
  )
}

export const canBookOrderAppointment = (order) => {
  if (!order || hasOrderAppointment(order)) return false

  const current = resolveFlowStatus(order)
  if (TERMINAL_STATUSES.has(current)) return false
  if (current !== ORDER_STATUS.PENDING && current !== ORDER_STATUS.CONFIRMATION) {
    return false
  }

  return isServiceOrder(order) || itemTypeFromRecord(order) === 'service'
}

export const getStatusStepState = (order, stepStatus) => {
  const flow = getStatusFlow(order)
  const current = resolveFlowStatus(order)
  const step = normalizeOrderStatus(stepStatus)
  const currentIdx = flow.indexOf(current)
  const stepIdx = flow.indexOf(step)

  if (currentIdx === -1) {
    return step === current ? 'current' : 'upcoming'
  }
  if (stepIdx < currentIdx) return 'complete'
  if (stepIdx === currentIdx) return 'current'
  return 'upcoming'
}

export const formatOrderStatusError = (error) => {
  const data = error?.response?.data
  if (!data) return error?.message || 'Request failed'
  if (typeof data === 'string') return data
  if (data.error) return data.error
  if (data.detail) return data.detail
  if (data.message) return data.message
  const messages = Object.entries(data).flatMap(([field, value]) => {
    if (Array.isArray(value)) return value.map((msg) => `${field}: ${msg}`)
    if (typeof value === 'string') return [`${field}: ${value}`]
    return []
  })
  return messages.length ? messages.join(', ') : 'Request failed'
}

export const patchOrderStatus = async (orderId, status) => {
  const res = await api.patch(`service-orders/orders/${orderId}/`, { status })
  return res.data
}
